import useBrainSearchBot from "@/admin/integrations/chat-bot/util/useBrainSearchBot"
import useStartNewBrainSearch from "@/admin/integrations/chat-bot/util/useStartNewBrainSearch"
import BrainSearchInput from "@/brain-search/internal/BrainSearchInput"
import { DashboardBlockItemCommonProps } from "@/dashboard/blocks/DashboardBlockItem"
import { useDashboardContext } from "@/dashboard/context/DashboardContext"
import useIsWebView from "@/product/util/hook/useIsWebView"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import useShowOnHoverStyles from "@assets/style/util/useShowOnHoverStyles"
import BannerImage from "@components/banner-image/BannerImage"
import { DiscoText } from "@disco-ui"
import { getContrastRatio, useTheme } from "@material-ui/core"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import classNames from "classnames"
import ReactDOM from "react-dom"
import { graphql, useFragment } from "react-relay"
import DashboardBlockAdminDropdown from "../DashboardBlockAdminDropdown"
import DashboardBlockItemTemplate from "./DashboardBlockItemTemplate"
import {
  DashboardBlockHeroMode,
  HeroDashboardBlockFragment$key,
} from "./__generated__/HeroDashboardBlockFragment.graphql"

interface Props extends DashboardBlockItemCommonProps {
  dashboardBlockKey: HeroDashboardBlockFragment$key
}

export default function HeroDashboardBlock({
  dashboardBlockKey,
  index,
  dashboardColumnHoveredShowableClass,
}: Props) {
  const showOnHoverClasses = useShowOnHoverStyles()
  const theme = useTheme()
  const TESTID = "HeroDashboardBlock"
  const { brainSearch, brainSearchSidePanel } = useFeatureFlags()
  const [form, redirectToAskAI] = useStartNewBrainSearch()
  const isWebView = useIsWebView()
  const brainSearchBot = useBrainSearchBot()
  const dashboard = useDashboardContext()!

  const block = useFragment<HeroDashboardBlockFragment$key>(
    graphql`
      fragment HeroDashboardBlockFragment on HeroDashboardBlock {
        id
        showWelcomeText
        askAi
        headerWithMacrosReplaced
        subText
        color
        heroMode: mode
        cover
        textColor
        ...DashboardBlockAdminDropdownFragment
        ...DashboardBlockItemTemplateFragment
      }
    `,
    dashboardBlockKey
  )

  const askAIEnabled =
    brainSearch &&
    block.askAi &&
    // Brain search is enabled on mobile via brainSearchSidePanel FF
    (!isWebView || brainSearchSidePanel)
  const classes = useStyles({
    askAIEnabled,
    textColor: block.textColor || undefined,
    heroMode: block.heroMode,
  })
  const isDark =
    block.heroMode === "color" && block.color
      ? getContrastRatio(theme.palette.common.white, block.color) > 1.5
      : false

  if (!block) return null

  const el = document.querySelector("#community-cover-banner-block-anchor")
  if (!el) return null
  return ReactDOM.createPortal(
    <DashboardBlockItemTemplate
      dashboardBlockKey={block}
      index={index}
      dashboardColumnHoveredShowableClass={dashboardColumnHoveredShowableClass}
    >
      <div className={classNames(classes.container, showOnHoverClasses.hoverable)}>
        {renderBanner()}
        <DashboardBlockAdminDropdown
          dashboardBlockKey={block}
          className={classNames(classes.dropdown, showOnHoverClasses.showable)}
        />

        {block.showWelcomeText && block.headerWithMacrosReplaced && (
          <div className={classes.header}>
            <DiscoText
              testid={`${TESTID}.header`}
              variant={"heading-lg"}
              color={isDark ? "common.white" : "common.black"}
              classes={{
                root: classes.headerText,
              }}
            >
              {block.headerWithMacrosReplaced}
            </DiscoText>
            <DiscoText
              testid={`${TESTID}.sub-text`}
              color={isDark ? "common.white" : "common.black"}
              classes={{
                root: classes.headerText,
              }}
            >
              {block.subText}
            </DiscoText>
            {dashboard.isForYou &&
              askAIEnabled &&
              brainSearchBot?.status === "active" && (
                <div className={classes.inputRoot}>
                  <BrainSearchInput
                    onSubmit={redirectToAskAI}
                    onChange={handleChangeQuery}
                    multiline={true}
                    classes={{
                      root: classes.input,
                    }}
                    enableSuggestions={true}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={false}
                  />
                </div>
              )}
          </div>
        )}
      </div>
    </DashboardBlockItemTemplate>,
    el
  )

  function renderBanner() {
    switch (block.heroMode) {
      case "no_fill":
        return (
          <div
            className={classNames(classes.cover, classes.colorFill)}
            style={{
              backgroundColor: theme.palette.background.default,
            }}
          />
        )
      case "color":
        return (
          <div
            className={classNames(classes.cover, classes.colorFill)}
            style={{
              backgroundColor: block.color!,
            }}
          />
        )
      case "image":
        return <BannerImage coverPhoto={block.cover!} className={classes.cover} />
      default:
        return null
    }
  }

  function handleChangeQuery(text: string) {
    form.state.title = text
  }
}

interface StyleProps {
  textColor?: string
  askAIEnabled: boolean
  heroMode: DashboardBlockHeroMode
}

const useStyles = makeUseStyles((theme) => ({
  container: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(0),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
    },
  },
  cover: {
    borderRadius: 0,
  },
  colorFill: ({ askAIEnabled, heroMode }: StyleProps) => ({
    height: "260px",
    ...styleIf(!askAIEnabled && heroMode === "no_fill", {
      height: "150px",
    }),
    [theme.breakpoints.down("sm")]: {
      height: "90px",
      ...styleIf(askAIEnabled, {
        height: "150px",
      }),
    },
  }),
  dropdown: {
    position: "absolute",
    top: theme.spacing(2.5),
    right: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
    zIndex: 2,
  },
  header: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    margin: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  headerText: ({ textColor }: StyleProps) => ({
    ...styleIf(textColor?.length, {
      color: textColor,
    }),
  }),
  inputRoot: {
    width: "100%",
    maxWidth: "689px",
    display: "flex",
    flexDirection: "column",
    alignSelf: "center",
    margin: "0 auto",
    marginTop: theme.spacing(3),
    gap: theme.spacing(4),
    padding: theme.spacing(0, 2),

    [theme.breakpoints.up("xl")]: {
      maxWidth: "1800px",
    },
  },
  input: {
    maxWidth: "485px",
    alignSelf: "center",

    [theme.breakpoints.up("xl")]: {
      width: "40%",
    },
  },
}))
